import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  styled,
  CircularProgress,
} from '@mui/material';
import UserSettingsAPI from '../../utilities/api/user/UserSettingsAPI';
import { notifyError, notifySuccess } from '../../utils/notifyToasts';
import { useSelector } from 'react-redux';

const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 44,
  height: 24,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 20,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(18px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#139C32',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 20,
    height: 20,
    borderRadius: 10,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 24 / 2,
    opacity: 1,
    backgroundColor: '#E0E0E0',
    boxSizing: 'border-box',
  },
}));

const SettingRow = ({ label, checked, onChange, name, indented = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, ml: indented ? { xs: 4, md: 8 } : 0 }}>
    <Typography sx={{ color: '#666666', fontSize: '15px', width: { xs: '200px', md: '300px' }, textAlign: 'left' }}>
      {label}
    </Typography>
    <CustomSwitch checked={checked} onChange={onChange} name={name} />
  </Box>
);

const NotificationContainer = () => {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({
    email_alerts: true,
    in_app_alerts: true,
    alert_booking_confirmation: false,
    alert_equipment_availability: false,
    alert_workshop_reminders: false,
    alert_upcoming_events: false,
    alert_in_app_messages: false,
  });

  const { user } = useSelector((state) => state.user || {});

  useEffect(() => {
    fetchSettings();
  }, [user?.user?.TL_name]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await UserSettingsAPI.getSettings();
      setState(res.data);
    } catch (error) {
      notifyError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (event) => {
    const { name, checked } = event.target;
    // Optimistic update
    setState((prevState) => ({ ...prevState, [name]: checked }));

    try {
      await UserSettingsAPI.updateSettings({ [name]: checked });
      notifySuccess('Setting updated');
    } catch (error) {
      notifyError('Failed to update setting');
      // Revert on error
      setState((prevState) => ({ ...prevState, [name]: !checked }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        p: { xs: 2, md: 4 },
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
        mt: 2,
        width: '100%',
        boxSizing: 'border-box',
        textAlign: 'left'
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ color: '#3F3F3F', fontSize: '16px', fontWeight: 600, mb: 3 }}>
          Alerts:
        </Typography>
        <SettingRow label="Email" name="email_alerts" checked={state.email_alerts} onChange={handleToggle} indented />
        {/* <SettingRow label="In-app" name="in_app_alerts" checked={state.in_app_alerts} onChange={handleToggle} indented /> */}
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography sx={{ color: '#3F3F3F', fontSize: '16px', fontWeight: 600, mb: 3 }}>
          Allow Alerts for:
        </Typography>
        <SettingRow label="Booking Confirmation" name="alert_booking_confirmation" checked={state.alert_booking_confirmation} onChange={handleToggle} indented />
        <SettingRow label="Equipment Availability" name="alert_equipment_availability" checked={state.alert_equipment_availability} onChange={handleToggle} indented />
        <SettingRow label="Workshop Reminders" name="alert_workshop_reminders" checked={state.alert_workshop_reminders} onChange={handleToggle} indented />
        <SettingRow label="Upcoming events" name="alert_upcoming_events" checked={state.alert_upcoming_events} onChange={handleToggle} indented />
        {/* <SettingRow label="In-app messages" name="alert_in_app_messages" checked={state.alert_in_app_messages} onChange={handleToggle} indented /> */}
      </Box>
    </Box>
  );
};

export default NotificationContainer;
